import { memo } from 'react';
import type { CompileResult } from '../engine/types';
import { useI18n } from '../i18n';
import { RowLabel } from './RowLabel';

export const CanonicalTab = memo(function CanonicalTab({
  result,
  activeLine,
  onJump,
}: {
  result: CompileResult;
  activeLine: number | null;
  onJump: (line: number) => void;
}) {
  const { t } = useI18n();
  if (result.rows.length === 0) return <p className="p-6 text-center text-sm text-muted">{t('canon.empty')}</p>;
  return (
    <div>
      <p className="border-b border-line px-4 py-2 text-2xs text-muted">{t('canon.caption')}</p>
      <table className="w-full border-separate border-spacing-0 text-xs" data-testid="canonical-table">
        <thead className="sticky top-0 z-10 bg-sunken text-left text-2xs uppercase tracking-[0.08em] text-subtle">
          <tr>
            <th scope="col" className="w-12 border-b border-line px-2 py-1.5 font-semibold">{t('rows.line')}</th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">{t('rows.label')}</th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">{t('canon.form')}</th>
            <th scope="col" className="border-b border-line px-2 py-1.5 font-semibold">{t('canon.rules')}</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row) => (
            <tr
              key={`${row.line_no}-${row.label}`}
              onClick={() => onJump(row.line_no)}
              className={`cursor-pointer align-top ${row.line_no === activeLine ? 'bg-accent/[0.09]' : 'even:bg-sunken/45 hover:bg-accent/[0.05]'}`}
            >
              <td className="border-b border-line/70 px-2 py-1.5">
                <button
                  type="button"
                  className="rounded px-1 font-mono text-2xs text-subtle hover:text-fg"
                  aria-label={t('diag.goto', { n: row.line_no })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onJump(row.line_no);
                  }}
                >
                  {row.line_no}
                </button>
              </td>
              <td className="border-b border-line/70 px-2 py-1.5">
                <RowLabel lineNo={row.line_no} label={row.label} result={result} className="block font-medium" />
                <div className="mt-0.5 flex flex-wrap gap-1 text-2xs">
                  <span className="rounded-pill bg-sunken px-2 text-subtle" title={row.kind}>
                    {t(`canon.kind.${row.kind}`)}
                  </span>
                  {row.understood ? (
                    <span className="rounded-pill bg-accent/[0.12] px-1.5 font-semibold text-accent-strong">✓ {t('canon.understood')}</span>
                  ) : (
                    <span className="rounded-pill bg-warning/15 px-1.5 font-semibold text-warning">? {t('canon.partial')}</span>
                  )}
                </div>
              </td>
              <td className="border-b border-line/70 px-2 py-1.5 font-mono text-[12px] text-fg" lang="en">
                <CanonicalText text={row.canonical} patternLang={result.document.lang} />
              </td>
              <td className="border-b border-line/70 px-2 py-1.5">
                <div className="flex flex-wrap gap-1">
                  {(row.translation_rules ?? []).map((rule) => (
                    <span key={rule} className="rounded-pill bg-sunken px-2 py-px font-mono text-2xs text-muted" data-verbatim>
                      {rule}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

/** Canonical English form; fragments the parser kept as written ([…] or «…») are pattern text. */
export function CanonicalText({ text, patternLang }: { text: string; patternLang: string }) {
  return (
    <>
      {text.split(/(\[[^\]]*\]|«[^»]*»)/).map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="italic text-muted" data-verbatim lang={patternLang}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
